
import { Bell, Check } from "lucide-react";
import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useNotifications } from "@/contexts/NotificationContext";
import TimelineApprovals from "@/components/timeline/TimelineApprovals";
import { useTimelineApprovals } from "@/hooks/useTimelineApprovals";

const Notifications = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const { pendingApprovals } = useTimelineApprovals();
  const isLoading = false; // Loading is now handled by the context

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'friend_request':
        return <Bell className="h-5 w-5 text-uplivion-blue" />;
      case 'new_entry':
        return <Bell className="h-5 w-5 text-green-500" />;
      default:
        return <Bell className="h-5 w-5 text-gray-500" />;
    }
  };

  const getNotificationMessage = (notification: any) => {
    switch (notification.type) {
      case 'friend_request':
        return 'Du hast eine neue Freundschaftsanfrage erhalten';
      case 'new_entry':
        return 'Jemand hat eine neue Erinnerung zu deiner Timeline hinzugefügt';
      case 'entry_approved':
        return 'Deine Timeline-Erinnerung wurde genehmigt';
      default:
        return notification.message || 'Neue Benachrichtigung';
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'vor wenigen Minuten';
    if (diffInHours < 24) return `vor ${diffInHours} Stunde${diffInHours > 1 ? 'n' : ''}`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `vor ${diffInDays} Tag${diffInDays > 1 ? 'en' : ''}`;
  };

  if (isLoading) {
    return (
      <AppShell showHeader={false} backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)">
        <div className="container mx-auto p-4">
          <div className="mb-6 mt-4">
            <h1 className="text-2xl font-bold text-uplivion-blue">Benachrichtigungen</h1>
          </div>
          
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Card key={i} className="uplivion-card">
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1">
                      <Skeleton className="h-4 w-3/4 mb-2" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell showHeader={false} backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)">
      <div className="container mx-auto p-4">
        <div className="mb-6 mt-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-uplivion-blue">Benachrichtigungen</h1>
            {unreadCount > 0 && (
              <p className="text-gray-600">{unreadCount} ungelesene Benachrichtigung{unreadCount > 1 ? 'en' : ''}</p>
            )}
          </div>
          
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={markAllAsRead}
            >
              <Check className="h-4 w-4 mr-2" />
              Alle als gelesen markieren
            </Button>
          )}
        </div>

        {/* Pending Approvals Section */}
        {pendingApprovals.length > 0 && (
          <div className="mb-6">
            <TimelineApprovals />
          </div>
        )}

        <div className="space-y-4">
          {notifications && notifications.length > 0 ? (
            notifications.map((notification) => (
              <Card 
                key={notification.id} 
                className={cn(
                  "cursor-pointer transition-colors uplivion-card",
                  !notification.is_read ? "bg-blue-50 border-blue-200" : ""
                )}
                onClick={() => !notification.is_read && markAsRead(notification.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start space-x-3">
                    <div className="mt-1">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1">
                      <p className={cn(
                        "text-sm",
                        !notification.is_read ? "font-medium" : "text-gray-600"
                      )}>
                        {getNotificationMessage(notification)}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatTime(notification.created_at)}
                      </p>
                    </div>
                    {!notification.is_read && (
                      <div className="w-2 h-2 bg-uplivion-blue rounded-full mt-2"></div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="uplivion-card">
              <CardContent className="p-8 text-center">
                <Bell className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Benachrichtigungen</h3>
                <p className="text-gray-500">Du hast aktuell keine Benachrichtigungen.</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AppShell>
  );
};

export default Notifications;
